import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button, Text } from '@frontend/design-system';
import { trackingId } from '../../../tracking';

export interface CallRouteActionBarProps {
  hasChanges: boolean;
  isSaving: boolean;
  isDeviceExtensionRoute: boolean;
  onSave: () => void;
  onUndo: () => void;
}

/**
 * Action bar displayed at the bottom of the call route details page
 * when there are unsaved changes.
 */
export const CallRouteActionBar = ({
  hasChanges,
  isSaving,
  isDeviceExtensionRoute,
  onSave,
  onUndo,
}: CallRouteActionBarProps) => {
  const { t } = useTranslation('phone');

  if (!hasChanges) {
    return null;
  }

  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        gap: ${theme.spacing(2)};
        padding: ${theme.spacing(2)};
        border-top: 1px solid ${theme.colors.neutral10};
      `}
    >
      <Text>{t('Changes were made')}</Text>
      <Button
        size='large'
        loading={isSaving}
        disabled={isSaving}
        onClick={onSave}
        css={css`
          width: 134px;
        `}
        trackingId={trackingId({
          context: 'setting',
          feature: isDeviceExtensionRoute ? 'device-extension-route' : 'call-route',
          details: 'save-changes-btn',
        })}
      >
        {t('Save Changes')}
      </Button>
      <Button
        size='large'
        disabled={isSaving}
        variant='secondary'
        onClick={onUndo}
        trackingId={trackingId({
          context: 'setting',
          feature: isDeviceExtensionRoute ? 'device-extension-route' : 'call-route',
          details: 'undo-changes-btn',
        })}
      >
        {t('Undo Changes')}
      </Button>
    </div>
  );
};
